import { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { onlineManager } from '@tanstack/react-query';

export type ConnectionQuality = 'good' | 'slow' | 'offline';

export interface ConnectivityContextValue {
  isOnline: boolean;
  connectionQuality: ConnectionQuality;
  lastOnlineAt: Date | null;
  checkConnectivity: () => Promise<boolean>;
}

export const ConnectivityContext = createContext<ConnectivityContextValue | null>(null);

interface ConnectivityProviderProps {
  children: ReactNode;
}

const PING_ENDPOINT = '/api/v1/health';
const PING_TIMEOUT = 5000;
const PING_INTERVAL = 30000; // Check every 30 seconds when online
// F-6: while offline the probe must keep running — recovery used to depend
// entirely on a browser `online` event, which never fires when the app went
// "offline" from a failed probe (SW hiccup, dropped request) rather than an
// OS-level disconnect. A stuck-offline app paused every react-query fetch
// forever; deep links and PWA cold starts spun on a loader until restart.
const PING_INTERVAL_OFFLINE = 10000;

export function ConnectivityProvider({ children }: ConnectivityProviderProps) {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>('good');
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(null);

  // Active connectivity check (not just navigator.onLine)
  const checkConnectivity = useCallback(async (): Promise<boolean> => {
    // First check navigator.onLine
    if (!navigator.onLine) {
      setIsOnline(false);
      setConnectionQuality('offline');
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT);

      const startTime = performance.now();

      const response = await fetch(PING_ENDPOINT, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const duration = performance.now() - startTime;

      if (response.ok) {
        setIsOnline(true);
        // Heal react-query's own online flag: if it latched offline (missed
        // `online` event, misreported navigator.onLine at boot), paused
        // queries resume the moment a probe proves the server reachable.
        onlineManager.setOnline(true);
        setLastOnlineAt(new Date());

        // Determine connection quality based on latency
        if (duration < 500) {
          setConnectionQuality('good');
        } else if (duration < 2000) {
          setConnectionQuality('slow');
        } else {
          setConnectionQuality('slow');
        }

        return true;
      }

      // Server error but network works
      setIsOnline(true);
      onlineManager.setOnline(true);
      setLastOnlineAt(new Date());
      setConnectionQuality('good');
      return true;
    } catch {
      // Network request failed
      setIsOnline(false);
      setConnectionQuality('offline');
      return false;
    }
  }, []);

  // Listen for browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      // Verify with active check
      checkConnectivity();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setConnectionQuality('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    checkConnectivity();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkConnectivity]);

  // Periodic connectivity check — never stops. Offline polls FASTER, because
  // that's exactly when the app needs to notice the network came back (F-6).
  useEffect(() => {
    const intervalId = setInterval(() => {
      checkConnectivity();
    }, isOnline ? PING_INTERVAL : PING_INTERVAL_OFFLINE);

    return () => clearInterval(intervalId);
  }, [isOnline, checkConnectivity]);

  const value: ConnectivityContextValue = {
    isOnline,
    connectionQuality,
    lastOnlineAt,
    checkConnectivity,
  };

  return (
    <ConnectivityContext.Provider value={value}>
      {children}
    </ConnectivityContext.Provider>
  );
}
